import { useEffect, useState } from 'react';

export interface PingStatus {
  isActive: boolean;
  status: 'ok' | 'timeout' | 'pending' | null;
  lastPingTime: number | null;
  interval: number;
  timeout: number;
  isWhatsAppTabTracked: boolean;
  isOnAllowedDomain?: boolean;
  keepAwakeEnabled?: boolean;
  /** The Blueticks account the extension is signed in to (newer builds; null = signed out). */
  signedInUserId?: string | null;
  /** True when the extension reports `signedInUserId` at all (older builds don't). */
  reportsSignedInUser?: boolean;
}

/**
 * The Blueticks extension's live ping status, asked over the existing
 * GET_PING_STATUS / PING_STATUS_UPDATE window channel (answered by the
 * extension's content script on app pages). `hasExtension` flips true on the
 * first answer — the one signal that the extension is installed in THIS browser,
 * even with no WhatsApp tab open yet.
 */
export function usePingStatus(): { hasExtension: boolean; status: PingStatus | null } {
  const [hasExtension, setHasExtension] = useState(false);
  const [status, setStatus] = useState<PingStatus | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PING_STATUS_UPDATE') {
        setHasExtension(true);
        setStatus(event.data.status ?? null);
      }
    };
    window.addEventListener('message', handleMessage);
    window.postMessage({ type: 'GET_PING_STATUS' }, '*');
    const id = setInterval(() => window.postMessage({ type: 'GET_PING_STATUS' }, '*'), 2000);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(id);
    };
  }, []);

  return { hasExtension, status };
}
